import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";

import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable } from "../../features/web3/forex/components/table";

import { useClosedTradesFeed, useForexRisk } from "./api";
import { FilterBar, FilterField, Money, PagePager } from "./parts";
import { COLUMNS, renderRow } from "./RiskReports";

// One account (login) or one user's accounts (username), every risk report on
// one page. The filter lives in the URL so a link opens straight onto it.
const FIELDS = ["login", "username"];
const PAGE_SIZE = "200";

const pick = (params) =>
  Object.fromEntries(FIELDS.map((f) => [f, params.get(f)?.trim() || ""]));

function Section({ title, count, children }) {
  return (
    <section className="space-y-3">
      <h2 className="flex items-baseline gap-2 text-sm font-semibold">
        {title}
        {count != null && <span className="font-mono text-xs text-muted-foreground">{count}</span>}
      </h2>
      {/* Each table scrolls on its own so one long list never buries the rest. */}
      <div className="max-h-[420px] overflow-y-auto rounded-md border border-border/60">{children}</div>
    </section>
  );
}

// Page-numbered reports: open positions and pending orders.
function PagedReport({ title, report, filters }) {
  const [page, setPage] = useState(1);
  const q = useForexRisk(report, { ...filters, pageSize: PAGE_SIZE, page });
  return (
    <Section title={title} count={q.data?.totalRows}>
      {q.error ? (
        <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          <QueueTable
            columns={COLUMNS[report]}
            isLoading={q.isLoading}
            rows={q.data?.data}
            renderRow={(row) => renderRow(report, row)}
          />
          <PagePager data={q.data} onChange={setPage} />
        </>
      )}
    </Section>
  );
}

function History({ filters }) {
  const profit = useForexRisk("profit", filters);
  const closed = useClosedTradesFeed({ ...filters, pageSize: PAGE_SIZE });
  const closedRows = closed.data?.pages.flatMap((p) => p.data);

  return (
    <>
      <Section title="Account details">
        {profit.error ? (
          <ErrorBanner error={profit.error} onRetry={() => profit.refetch()} />
        ) : (
          <QueueTable
            columns={COLUMNS.profit}
            isLoading={profit.isLoading}
            rows={profit.data?.data}
            renderRow={(row) => renderRow("profit", row)}
          />
        )}
      </Section>
      {!profit.error && (
        <div className="flex gap-8 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">Accounts</div>
            {profit.data ? (
              <div className="mt-1 font-mono text-lg">{profit.data.accounts}</div>
            ) : (
              <Skeleton className="mt-1.5 h-6 w-10" />
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">Total profit</div>
            {profit.data ? (
              <div className="mt-1 text-lg"><Money value={profit.data.total} /></div>
            ) : (
              <Skeleton className="mt-1.5 h-6 w-20" />
            )}
          </div>
        </div>
      )}

      <PagedReport title="Open positions" report="open-positions" filters={filters} />

      {/* No total count from the API, so the count is what has loaded so far. */}
      <Section title="Closed positions" count={closedRows?.length}>
        {closed.error ? (
          <ErrorBanner error={closed.error} onRetry={() => closed.refetch()} />
        ) : (
          <>
            <QueueTable
              columns={COLUMNS["closed-trades"]}
              isLoading={closed.isLoading}
              rows={closedRows}
              renderRow={(row) => renderRow("closed-trades", row)}
            />
            {closed.hasNextPage && (
              <div className="flex justify-center py-3">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={closed.isFetchingNextPage}
                  onClick={() => closed.fetchNextPage()}
                >
                  {closed.isFetchingNextPage ? "Loading…" : "Load more"}
                </Button>
              </div>
            )}
          </>
        )}
      </Section>

      <PagedReport title="Pending orders" report="pending-orders" filters={filters} />
    </>
  );
}

export default function AccountHistory() {
  const [params, setParams] = useSearchParams();
  const filters = pick(params);
  const [draft, setDraft] = useState(filters);
  const queryClient = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-risk"] }) > 0;

  // A login/username link on this page changes the URL: the form follows it.
  const search = params.toString();
  useEffect(() => setDraft(pick(new URLSearchParams(search))), [search]);

  const apply = () =>
    setParams(Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v)));

  const hasFilter = FIELDS.some((f) => filters[f]);

  return (
    <QueueLayout
      title="Account"
      accent="history"
      description="Everything one MT5 login, or every account of one username, is carrying: open positions, closed positions and pending orders. Real-money accounts only."
      isFetching={fetching}
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ["forex-risk"] })}
    >
      <FilterBar onApply={apply} onReset={() => setParams({})}>
        <FilterField label="MT5 login" name="login" draft={draft} setDraft={setDraft} placeholder="261077" />
        <FilterField label="Username" name="username" draft={draft} setDraft={setDraft} placeholder="217831" />
      </FilterBar>

      {hasFilter ? (
        // Keyed so paging and loaded pages start over for a new account.
        <History key={search} filters={filters} />
      ) : (
        <p className="py-16 text-center text-sm text-muted-foreground">
          Enter an MT5 login or a username to see its history.
        </p>
      )}
    </QueueLayout>
  );
}
